import { Controller, useFormContext } from 'react-hook-form';

import { FormControl, FormControlProps } from '../ui/FormControl';
import { Select, SelectProps } from '../ui/Select';

type OptionsType = Array<{
  label: string;
  value: string | number;
}>;

export type SelectDateFieldProps = FormControlProps &
  SelectProps & {
    dayOptions: OptionsType;
    monthOptions: OptionsType;
    yearOptions: OptionsType;
  };

const SelectDateField = (props: SelectDateFieldProps) => {
  const { control } = useFormContext();
  const {
    name,
    onChange,
    label,
    caption,
    detail,
    isDisabled,
    isRequired,
    dayOptions,
    monthOptions,
    yearOptions,
    ...fieldProps
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { value, onChange: onChangeHookForm, onBlur },
        formState: { isSubmitted },
        fieldState: { isTouched, error },
      }) => {
        const hasError = error && (isTouched || isSubmitted);
        const [day, month, year] = (value || '').split('/');

        const onSelectBlur = () => {
          if (!day || !month || !year) return;

          return onBlur();
        };

        return (
          <FormControl
            name={name}
            label={label}
            detail={detail}
            caption={hasError ? error.message : caption}
            isDisabled={isDisabled}
            isInvalid={hasError}
          >
            <div className="grid grid-cols-3 gap-1">
              <Select
                {...fieldProps}
                name={name}
                id={name}
                value={day || ''}
                onChange={(event) => {
                  const eventWithValue = {
                    ...event,
                    target: {
                      ...event.target,
                      value: `${event.target.value}/${month || ''}/${year || ''}`,
                    },
                  };

                  onChangeHookForm(eventWithValue);

                  if (onChange) onChange(eventWithValue);
                }}
                onBlur={onSelectBlur}
                isInvalid={hasError}
                isRequired={isRequired}
                disabled={isDisabled}
              >
                <option
                  value=""
                  disabled
                >
                  Dia
                </option>
                {dayOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </Select>

              <Select
                {...fieldProps}
                name={`${name}-month`}
                id={`${name}-month`}
                value={month || ''}
                onChange={(event) => {
                  const eventWithValue = {
                    ...event,
                    target: {
                      ...event.target,
                      value: `${day || ''}/${event.target.value}/${year || ''}`,
                    },
                  };

                  onChangeHookForm(eventWithValue);

                  if (onChange) onChange(eventWithValue);
                }}
                onBlur={onSelectBlur}
                isInvalid={hasError}
                isRequired={isRequired}
                disabled={isDisabled}
              >
                <option
                  value=""
                  disabled
                >
                  Mês
                </option>
                {monthOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </Select>

              <Select
                {...fieldProps}
                name={`${name}-year`}
                id={`${name}-year`}
                value={year || ''}
                onChange={(event) => {
                  const eventWithValue = {
                    ...event,
                    target: {
                      ...event.target,
                      value: `${day || ''}/${month || ''}/${event.target.value}`,
                    },
                  };

                  onChangeHookForm(eventWithValue);

                  if (onChange) onChange(eventWithValue);
                }}
                onBlur={onSelectBlur}
                isInvalid={hasError}
                isRequired={isRequired}
                disabled={isDisabled}
              >
                <option
                  value=""
                  disabled
                >
                  Ano
                </option>
                {yearOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </FormControl>
        );
      }}
    />
  );
};

SelectDateField.displayName = 'SelectDateField';

export { SelectDateField };
